
















































import 'quill/dist/quill.bubble.css'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

import { Guid } from 'guid-typescript'
import { debounce } from 'lodash'
import isMobile from 'piramis-js-utils/lib/isMobile'
import Quill, { QuillOptionsStatic, RangeStatic } from 'quill'
import VueFullscreen, { component as Fullscreen } from 'vue-fullscreen'
import { Component, Mixins, Ref, VModel, Watch } from 'vue-property-decorator'
import { quillEditor as QuillEditor } from 'vue-quill-editor'

import Toolbar from './components/Toolbar/Toolbar.vue'
import QuillSetup from './includes/quill-setup'
import TgHelper from './includes/tg-helper'
import EditorProps from './mixins/mixins'
import VueFullscreenApi = VueFullscreen.VueFullscreenApi

QuillSetup.init()

@Component({
  components: {
    QuillEditor,
    Toolbar,
    Fullscreen
  },
  methods: {
    isMobile,
  },
})
export default class Editor extends Mixins<EditorProps>(EditorProps) {
  @VModel() model!: string

  @Ref('fullscreen') readonly fullscreen!: VueFullscreenApi

  guid = Guid.create().toString()

  counter = 0

  editor: Quill | null = null

  isFullscreen = false

  canInput = true

  get editorConfig(): QuillOptionsStatic {
    return {
      formats: [
        'bold',
        'code',
        'italic',
        'link',
        'strike',
        'script',
        'underline',
        'code-block',
        'tgpre',
        'tgspoiler',
        'blockquote',
        'blockquoteExpandable',
        'emoji',
      ],
      modules: {
        toolbar: {
          container: `#toolbar-${ this.guid }`,
        },
        'emoji-toolbar': true,
        // "emoji-textarea": true,
        // 'emoji-shortname': true,
        history: {
          delay: 2000,
          maxStack: 500,
          userOnly: true,
        },
      },
    }
  }

  @Watch('model')
  onModelUpdate(value: string): void {
    this.canInput = false
    this.updateQuillEditorModel(value)
    // eslint-disable-next-line no-return-assign
    this.$nextTick((): boolean => this.canInput = true)
  }

  setPlaceholder(placeholder: string): void {
    if (this.editor?.getSelection(true)) {
      const sel: RangeStatic = this.editor?.getSelection(true)
      const text = this.editor?.getText(0, this.editor?.getLength())

      if (sel.length === 0) {
        this.editor?.insertText(sel.index, this.getInsertEnvironment(sel.index, text, placeholder))
      } else {
        const sliceText = {
          start: text?.slice(0, sel.index),
          end: text?.slice(sel.index + sel.length, text?.length),
        }

        this.editor.setContents(TgHelper.convertTelegramHtmlToQuillContents(`${ sliceText.start } ${ placeholder } ${ sliceText.end }`))
      }
    }
  }

  getInsertEnvironment(index: number, text: string, placeholder: string): string {
    if (index === 0) {
      return `${ placeholder } `
    } else {
      if (text[index] !== ' ' && text[index - 1] !== ' ') {
        return ` ${ placeholder } `

      } else if (text[index] === ' ' && text[index - 1] !== ' ') {
        return ` ${ placeholder }`
      } else {
        return placeholder
      }
    }
  }

  updateQuillEditorModel(value: string): void {
    if (this.editor && !this.editor.hasFocus()) {
      this.editor.setContents(TgHelper.convertTelegramHtmlToQuillContents(value))
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleQuill(editor: Quill): void {
    // Add editor instance to the component scope
    this.editor = editor

    this.updateQuillEditorModel(this.model)

    if (this.trimEnd) {
      this.model = TgHelper.convertQuillContentsToTelegramHtml(this.editor.getContents()).trimEnd()
    }

    this.toggleQuill()

    editor.on('editor-change', (eventName: any, args: any) => {
      if (eventName === 'text-change') {
        if (editor.getLength() >= this.counterMax) {
          editor.deleteText(this.counterMax, editor.getLength())
        }

        if (editor.getText().length > 1025) {
          this.$emit('uploadDisabled', true)
        } else {
          this.$emit('uploadDisabled', false)
        }
      }
    })

    editor.on('text-change', debounce(() => {
      const delta = editor.getContents()

      if (this.canInput) {
        let text: string = TgHelper.convertQuillContentsToTelegramHtml(delta)

        if (this.trimEnd) {
          text = text.trimEnd()
        }

        this.model = text
      }
    }, 250))
  }

  get trimQuillText(): number {
    const quillAutoAddedRedundantNewLine = 1

    if (this.editor) {
      this.counter = this.editor.getLength() - quillAutoAddedRedundantNewLine

      return this.counter
    }
    return 0
  }

  @Watch('disabled')
  toggleQuill(): void {
    const { editor } = this

    if (editor) {
      if (this.disabled) {
        editor.disable()
      } else {
        editor.enable()
      }
    }
  }

  toggleFullscreen(): void {
    if (this.fullscreen) {
      this.fullscreen.toggle()
    }
  }
}
